import {
  Box,
  TextField,
  Button,
  Grid,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  CircularProgress,
  Card,
  CardContent,
  useTheme,
  IconButton
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { formatCurrency } from 'src/common/utils/currency';
import { useSnackbar } from 'src/components/snackbar/useSnackbar';
import { useWalletContext } from 'src/components/walletcontexthook';
import { authenticationService } from 'src/services';
import { get, post } from 'src/services/httpClient.service';
import { getResponsiveStyles, isMobileScreen } from 'src/common/utils/responsive';
import {FormControl,InputLabel,Select,MenuItem} from '@mui/material';

const PaymentTransfer = () => {
  const theme = useTheme();
  const styles = getResponsiveStyles(theme);
  const [isMobile, setIsMobile] = useState(isMobileScreen(theme));
  const { showSnackbar } = useSnackbar();
  const APIURL = process.env.REACT_APP_BASE_API_URL;
  const navigate = useNavigate();
  const { vendorId, franchiseId } = authenticationService.getVendorFranchiseIds();
  const [transactionType, setTransactionType] = useState('CREDIT');

  const accountType = localStorage.getItem('accountType');

  const { fetchOperatorDetails } = useWalletContext();

  const [operatorData] = useState(() =>
    JSON.parse(localStorage.getItem('operatorData') || '{}')
  );

  console.log('operatorData', operatorData);

  const transferredBy = operatorData?.id;
  const transferredName = operatorData?.operatorName;

  const [operators, setOperators] = useState([]);
  const [amounts, setAmounts] = useState({});

  // Loading states
  const [loading, setLoading] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);

  // Pagination states
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(100);
  const [expandedCards, setExpandedCards] = useState({});

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(isMobileScreen(theme));
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [theme]);

  // Fetch operators
  useEffect(() => {
    const fetchOperators = async () => {
      setLoading(true);
      try {
        let response = null;

        if (accountType === "OWNER") {
          response = await get(
            `${APIURL}/api/operators/vendor/${vendorId}/getOwnerLcoWalletAccessList`
          );
        } else {
          response = await get(
            `${APIURL}/api/operators/vendor/${vendorId}/getDistributorLcoWalletAccessList`,
            { params: { distributorId: transferredBy } }
          );
        }

        setOperators(response?.data || []);
      } catch (error) {
        console.error(error);
        showSnackbar('Failed to fetch operators', 'error');
      } finally {
        setLoading(false);
      }
    };

    fetchOperators();
  }, []);

  // Handle amount change
  const handleAmountChange = (id, value) => {
    setAmounts((prev) => ({
      ...prev,
      [id]: value
    }));
  };

  // Pagination handlers
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Submit all
  const handleSubmit = async () => {
    try {
      setSubmitLoading(true);

      const payload = operators
        .filter((op) => Number(amounts[op.id]) > 0)
        .map((op) => ({
          afterBalance: 0,
          beforeBalance: 0,
          distributorBalance: 0,
          franchiseId,
          operatorId: op.id,
          operatorName: op.operatorName,
          transferedAmount: Number(amounts[op.id]),
          transferredBy,
          transferredName,
          transactionType,
          vendorId
        }));

      if (payload.length === 0) {
        showSnackbar('Please enter at least one amount', 'warning');
        setSubmitLoading(false);
        return;
      }

      await post(`${APIURL}/api/payment-transfer-history/create`, payload);

      await fetchOperatorDetails();
      showSnackbar('All transfers successful', 'success');
      navigate('/paymentTransfer/PaymentHistoryList');
    } catch (err) {
      console.error(err);
      showSnackbar('Error in transfer', 'error');
    } finally {
      setSubmitLoading(false);
    }
  };

  // Mobile card render
  const renderOperatorCard = (op) => {
    const isExpanded = expandedCards[op.id];

    return (
      <Card key={op.id} sx={styles.mobileCard}>
        <Box sx={styles.mobileCardHeader}>
          <Box>
            <Typography sx={styles.mobileCardTitle}>
              {op.operatorName}
            </Typography>
            <Typography sx={styles.mobileCardSubText}>
              Franchise: {op.franchiseName || '-'}
            </Typography>
          </Box>
        </Box>

        <Typography sx={{ ...styles.mobileCardText, mt: 1 }}>
          📋 Account Type: {op.accountType || 'N/A'}
        </Typography>

        <Typography sx={{ ...styles.mobileCardText, mt: 0.5 }}>
          💰 Wallet Amount: {formatCurrency(op.walletAmount)}
        </Typography>

        <Box sx={{ mt: 1 }}>
          <Button
            size="small"
            onClick={() =>
              setExpandedCards((prev) => ({
                ...prev,
                [op.id]: !prev[op.id]
              }))
            }
          >
            {isExpanded ? 'Hide Details' : 'View More'}
          </Button>
        </Box>

        {isExpanded && (
          <Box sx={{ mt: 1.5, fontSize: '13px', opacity: 0.9 }}>
            <Typography>Operator ID: {op.id}</Typography>
            {op.email && <Typography>Email: {op.email}</Typography>}
            {op.phone && <Typography>Phone: {op.phone}</Typography>}
          </Box>
        )}

        <Box sx={{ mt: 2 }}>
          <TextField
            label="LCO Amount"
            type="text"
            size="small"
            value={amounts[op.id] || ''}
            onChange={(e) => {
              const value = e.target.value;
              if (/^\d*$/.test(value)) {
                handleAmountChange(op.id, value);
              }
            }}
            inputProps={{
              inputMode: 'numeric',
              pattern: '[0-9]*'
            }}
            fullWidth
          />
        </Box>
      </Card>
    );
  };

  const paginatedOperators = operators.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      {/* Sender Info */}
      <Paper sx={{ p: { xs: 2, sm: 3 }, mb: 2 }}>
        <Typography 
          variant={isMobile ? "subtitle1" : "h6"} 
          gutterBottom 
          sx={{ fontWeight: 600, mb: 2 }}
        >
          Transfer Information
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <TextField
              label="Transferred By"
              value={transferredName || 'N/A'}
              fullWidth
              disabled
              size={isMobile ? "small" : "medium"}
            />
          </Grid>

          <Grid item xs={12} md={6}>
    <FormControl fullWidth size={isMobile ? "small" : "medium"}>
      <InputLabel>Transaction Type</InputLabel>
      <Select
        value={transactionType}
        label="Transaction Type"
        onChange={(e) => setTransactionType(e.target.value)}
      >
        <MenuItem value="CREDIT">CREDIT</MenuItem>
        <MenuItem value="DEBIT">DEBIT</MenuItem>
      </Select>
    </FormControl>
  </Grid>
        </Grid>
      </Paper>

      {/* Operator List */}
      <Paper sx={{ p: { xs: 2, sm: 3 } }}>
        <Typography 
          variant={isMobile ? "subtitle1" : "h6"} 
          gutterBottom 
          sx={{ fontWeight: 600, mb: 2 }}
        >
          Operator List
        </Typography>

        {/* Loading Spinner */}
        {loading ? (
          <Box display="flex" justifyContent="center" p={3}>
            <CircularProgress />
          </Box>
        ) : operators.length === 0 ? (
          <Box display="flex" justifyContent="center" p={3}>
            <Typography color="text.secondary">No operators found</Typography>
          </Box>
        ) : (
          <>
            {isMobile ? (
              // 📱 MOBILE VIEW - Card Layout
              <Box sx={styles.mobileCardContainer}>
                {paginatedOperators.map(renderOperatorCard)}

                {/* Pagination for mobile */}
                {operators.length > rowsPerPage && (
                  <Box sx={styles.mobileLoadMore}>
                    <Button
                      variant="contained"
                      onClick={() => {
                        if (page + 1 < Math.ceil(operators.length / rowsPerPage)) {
                          setPage(page + 1);
                        }
                      }}
                      disabled={page + 1 >= Math.ceil(operators.length / rowsPerPage)}
                    >
                      Load More ({paginatedOperators.length}/{operators.length})
                    </Button>
                  </Box>
                )}
              </Box>
            ) : (
              // 💻 DESKTOP VIEW - Table Layout
              <>
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell><b>Operator Name</b></TableCell>
                        <TableCell><b>Franchise</b></TableCell>
                        <TableCell><b>Account Type</b></TableCell>
                        <TableCell><b>Wallet Amount</b></TableCell>
                        <TableCell><b>Distribute Amount</b></TableCell>
                      </TableRow>
                    </TableHead>

                    <TableBody>
                      {paginatedOperators.map((op) => (
                        <TableRow key={op.id}>
                          <TableCell>{op.operatorName}</TableCell>
                          <TableCell>{op.franchiseName || '-'}</TableCell>
                          <TableCell>{op.accountType || 'N/A'}</TableCell>
                          <TableCell>{formatCurrency(op.walletAmount)}</TableCell>
                          <TableCell>
                            <TextField
                              label="LCO Amount"
                              type="text"
                              size="small"
                              value={amounts[op.id] || ''}
                              onChange={(e) => {
                                const value = e.target.value;
                                if (/^\d*$/.test(value)) {
                                  handleAmountChange(op.id, value);
                                }
                              }}
                              inputProps={{
                                inputMode: 'numeric',
                                pattern: '[0-9]*'
                              }}
                              sx={{ width: '150px' }}
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>

                {/* Pagination */}
                <TablePagination
                  component="div"
                  count={operators.length}
                  page={page}
                  onPageChange={handleChangePage}
                  rowsPerPage={rowsPerPage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  rowsPerPageOptions={[5, 10, 25, 50, 100]}
                />
              </>
            )}

            {/* Submit Buttons */}
            <Box 
              mt={3} 
              sx={{ 
                display: "flex", 
                gap: "10px", 
                justifyContent: "flex-end",
                flexDirection: isMobile ? 'column-reverse' : 'row'
              }}
            >
              <Button
                variant="outlined"
                onClick={() => navigate('/paymentTransfer/PaymentHistoryList')}
                disabled={submitLoading}
                fullWidth={isMobile}
                sx={{ py: isMobile ? 1 : 1.5 }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handleSubmit}
                disabled={submitLoading}
                fullWidth={isMobile}
                sx={{ py: isMobile ? 1 : 1.5 }}
              >
                {submitLoading ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  'Submit All'
                )}
              </Button>
            </Box>
          </>
        )}
      </Paper>
    </Box>
  );
};

export default PaymentTransfer;